import React, {
  FunctionComponent,
  ChangeEvent,
  useState,
  useEffect,
  useRef,
} from "react";
import { EnrollmentFormProps } from "./../EnrollmentFormPage";
import BackNextButtons from "./../BackNextButtons";
import { FocusableHeader } from "../../../components";
import {
  useTranslate,
  getValidationObject,
  EnrollmentPages,
  limitInputLength,
} from "../../../helpers";
import { TextField, Choice, Dropdown, Alert } from "@cmsgov/ds-medicare-gov";
import states from "../states";
import PlanInfo from "../PlanInfo";
import { removeExtraWhiteSpace } from "../../../helpers/enrollmentFormHelpers";

const Address: FunctionComponent<EnrollmentFormProps> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  inReview,
  plan,
}) => {
  const t = useTranslate();
  const alertDiv = useRef<HTMLDivElement>(null);
  const statesWithEmptyEntry = [{ name: "", fipsCode: "" }, ...states];
  const [validForm, setValidForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [addressError, setAddressError] = useState<string | undefined>(
    undefined
  );
  const [addressTouched, setAddressTouched] = useState(false);
  const [mailingAddressError, setMailingAddressError] = useState<
    string | undefined
  >(undefined);
  const [mailingAddressTouched, setMailingAddressTouched] = useState(false);
  const [cityError, setCityError] = useState<string | undefined>(undefined);
  const [cityTouched, setCityTouched] = useState(false);
  const [mailingCityError, setMailingCityError] = useState<string | undefined>(
    undefined
  );
  const [mailingCityTouched, setMailingCityTouched] = useState(false);
  const [zipError, setZipError] = useState<string | undefined>(undefined);
  const [mailingZipError, setMailingZipError] = useState<string | undefined>(
    undefined
  );
  const [mailingZipTouched, setMailingZipTouched] = useState(false);
  const [stateError, setStateError] = useState<string | undefined>(undefined);
  const [stateTouched, setStateTouched] = useState(false);
  const [mailingStateError, setMailingStateError] = useState<
    string | undefined
  >(undefined);
  const [mailingStateTouched, setMailingStateTouched] = useState(false);

  const validateForm = (): void => {
    const formStatus = getValidationObject(
      enrollData,
      plan,
      EnrollmentPages.Address
    );

    if (!formStatus.addressValidity && addressTouched) {
      setAddressError(t("enroll_form.enter_address"));
    } else {
      setAddressError(undefined);
    }

    if (!formStatus.cityValidity && cityTouched) {
      setCityError(t("enroll_form.enter_city"));
    } else {
      setCityError(undefined);
    }

    if (!formStatus.zipValidity) {
      setZipError(t("enroll_form.enter_zip"));
    } else {
      setZipError(undefined);
    }

    if (!formStatus.stateValidity && stateTouched) {
      setStateError(t("enroll_form.select_state"));
    } else {
      setStateError(undefined);
    }

    if (enrollData.differentMailingAddress) {
      if (!formStatus.mailingAddressValidity && mailingAddressTouched) {
        setMailingAddressError(t("enroll_form.enter_address"));
      } else {
        setMailingAddressError(undefined);
      }

      if (!formStatus.mailingCityValidity && mailingCityTouched) {
        setMailingCityError(t("enroll_form.enter_city"));
      } else {
        setMailingCityError(undefined);
      }

      if (!formStatus.mailingStateValidity && mailingStateTouched) {
        setMailingStateError(t("enroll_form.select_state"));
      } else {
        setMailingStateError(undefined);
      }

      if (!formStatus.mailingZipValidity && mailingZipTouched) {
        setMailingZipError(t("enroll_form.enter_zip"));
      } else {
        setMailingZipError(undefined);
      }
    }

    setValidForm(formStatus.isValid);
  };

  useEffect(() => {
    validateForm();
  }, [
    addressTouched,
    cityTouched,
    stateTouched,
    mailingAddressTouched,
    mailingZipTouched,
    mailingCityTouched,
    mailingStateTouched,
  ]);

  useEffect(() => {
    validateForm();
  }, [enrollData]);

  const next = (): void => {
    const {
      differentMailingAddress,
      address1: mailingAddress1,
      address2: mailingAddress2,
      city: mailingCity,
      state: mailingState,
      zip: mailingZip,
    } = enrollData;

    setSubmitted(true);

    if (!validForm) {
      setAddressTouched(true);
      setCityTouched(true);
      setStateTouched(true);
      setMailingAddressTouched(true);
      setMailingZipTouched(true);
      setMailingCityTouched(true);
      setMailingStateTouched(true);

      if (alertDiv.current) {
        alertDiv.current.scrollIntoView();
      }

      return;
    }

    if (!differentMailingAddress) {
      setEnrollData({
        ...enrollData,
        mailingAddress1,
        mailingAddress2,
        mailingCity,
        mailingState,
        mailingZip,
      });
    }

    nextPage();
  };

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.your_address")}
      />

      <PlanInfo plan={plan} />

      <span className="sr-only">
        {t("sr_only_additional_questions_may_appear")}
      </span>

      <div ref={alertDiv}>
        {!validForm && submitted && (
          <Alert
            className="ds-u-margin-top--2"
            heading={t("enroll_form.needs_your_attention")}
            variation="error"
          >
            {t("enroll_form.complete_required")}
          </Alert>
        )}
      </div>

      <h2 className="ds-u-margin-bottom--0">
        {t("enroll_form.perm_residence")}
      </h2>
      <p className="ds-u-margin-y--0">{t("enroll_form.no_po_box")}</p>

      <TextField
        errorMessage={addressError}
        label={t("enroll_form.address_line_1")}
        onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
          setEnrollData({
            ...enrollData,
            address1: removeExtraWhiteSpace(e.target.value),
          });
          setAddressTouched(true);
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>): void => {
          setEnrollData({
            ...enrollData,
            address1: e.target.value,
          });
          setAddressTouched(true);
        }}
        value={enrollData.address1}
        name="address-1"
      />

      <TextField
        label={`${t("enroll_form.address_line_2")} ${t(
          "enroll_form.optional"
        )}`}
        onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
          setEnrollData({
            ...enrollData,
            address2: removeExtraWhiteSpace(e.target.value),
          });
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>): void => {
          setEnrollData({
            ...enrollData,
            address2: e.target.value,
          });
        }}
        size="medium"
        value={enrollData.address2}
        name="address-2"
      />

      <TextField
        errorMessage={cityError}
        label={t("enroll_form.city")}
        onChange={(e: ChangeEvent<HTMLInputElement>): void => {
          setEnrollData({
            ...enrollData,
            city: e.target.value,
          });
          setCityTouched(true);
        }}
        onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
          setEnrollData({
            ...enrollData,
            city: removeExtraWhiteSpace(e.target.value),
          });
          setCityTouched(true);
        }}
        value={enrollData.city}
        name="city"
      />

      <Dropdown
        options={statesWithEmptyEntry.map(state => {
          return { label: state.name, value: state.fipsCode };
        })}
        value={enrollData.state}
        errorMessage={stateError}
        label={t("enroll_form.state")}
        name="state"
        onBlur={(): void => setStateTouched(true)}
        onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
          setEnrollData({
            ...enrollData,
            state: e.currentTarget.value,
          });
          setStateTouched(true);
        }}
      />

      <TextField
        label={t("enroll_form.zip_county")}
        size="medium"
        value={`${enrollData.zip}${
          enrollData.county ? `, ${enrollData.county.name}` : ""
        }`}
        name="zip-and-county"
        disabled
        errorMessage={zipError}
        hint={t("enroll_form.zip_county_disclaimer")}
      />

      <div className="ds-u-margin-top--3">
        <Choice
          type="checkbox"
          name="mailing-same-as-permanent"
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            setEnrollData({
              ...enrollData,
              differentMailingAddress: e.currentTarget.checked,
            });
          }}
          checked={enrollData.differentMailingAddress}
          value="mailing-question"
        >
          {t("enroll_form.different_mailing_address")}
        </Choice>
      </div>

      {enrollData.differentMailingAddress && (
        <>
          <h2 className="ds-u-margin-top--7 ds-u-margin-bottom--0">
            {t("enroll_form.mailing_address")}
          </h2>

          <TextField
            errorMessage={mailingAddressError}
            label={t("enroll_form.address_line_1")}
            onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                mailingAddress1: removeExtraWhiteSpace(e.target.value),
              });
              setMailingAddressTouched(true);
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                mailingAddress1: e.target.value,
              });
              setMailingAddressTouched(true);
            }}
            value={enrollData.mailingAddress1}
            name="mailing-address-1"
          />

          <TextField
            label={`${t("enroll_form.address_line_2")} ${t(
              "enroll_form.optional"
            )}`}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                mailingAddress2: e.target.value,
              });
            }}
            onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                mailingAddress2: removeExtraWhiteSpace(e.target.value),
              });
            }}
            size="medium"
            value={enrollData.mailingAddress2}
            name="mailing-address-2"
          />

          <TextField
            errorMessage={mailingCityError}
            label={t("enroll_form.city")}
            onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                mailingCity: removeExtraWhiteSpace(e.target.value),
              });
              setMailingCityTouched(true);
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                mailingCity: e.target.value,
              });
              setMailingCityTouched(true);
            }}
            value={enrollData.mailingCity}
            name="mailing-city"
          />

          <Dropdown
            errorMessage={mailingStateError}
            options={statesWithEmptyEntry.map(state => {
              return { label: state.name, value: state.fipsCode };
            })}
            value={enrollData.mailingState}
            label={t("enroll_form.state")}
            name="mailing-state"
            onBlur={(): void => setMailingStateTouched(true)}
            onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
              setEnrollData({
                ...enrollData,
                mailingState: e.currentTarget.value,
              });
              setMailingStateTouched(true);
            }}
          />

          <TextField
            errorMessage={mailingZipError}
            label={t("enroll_form.zip")}
            fieldClassName="ds-c-field--zipCode"
            onBlur={(): void => setMailingZipTouched(true)}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              limitInputLength("zipCode", 5);
              setEnrollData({
                ...enrollData,
                mailingZip: e.target.value,
              });
              setMailingZipTouched(true);
            }}
            value={enrollData.mailingZip}
            name="mailing-zip"
          />
        </>
      )}

      <BackNextButtons
        inReview={inReview}
        nextPage={next}
        previousPage={previousPage}
      />
    </div>
  );
};

export default Address;
